import { Box, Button, Divider, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApiMessageStack, FormControlV2 } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
  DEFAULT_WARNING_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { observer } from "mobx-react";
import { FC, useCallback } from "react";
import { useForm } from "react-hook-form";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import {
  jupixIntegrationConnectValidationSchema,
  jupixIntegrationInitialValues,
  TJupixIntegrationConfigFieldValues,
} from "utils/validation-schemas/jupix-integration-validation";
import { SETTINGS_INTEGRATIONS_ROUTE } from "../../../../../constants/routes";
import { useHistory } from "react-router-dom";

interface IProps {
  accountIntegrationsStore: AccountIntegrationsStore;
  closePrompt: () => void;
}

export const ConnectJupixForm: FC<IProps> = observer(
  ({ accountIntegrationsStore, closePrompt }) => {
    const toast = useToast();
    const history = useHistory();

    const {
      handleSubmit,
      control,
      formState: { isSubmitting },
    } = useForm<TJupixIntegrationConfigFieldValues>({
      defaultValues: jupixIntegrationInitialValues,
      mode: "onSubmit",
      resolver: yupResolver(jupixIntegrationConnectValidationSchema),
    });

    const onSubmit = useCallback(
      async (integrationConfig: TJupixIntegrationConfigFieldValues) => {
        try {
          await accountIntegrationsStore.createAccountIntegration<
            TJupixIntegrationConfigFieldValues
          >({
            type: AccountIntegrationTypeEnum.JUPIX,
            active: true,
            integrationConfig,
          });
        } catch (e) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={e?.message} />,
          });
          return;
        }

        try {
          await accountIntegrationsStore.connectJupix();
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={"Jupix connected."} />,
          });
        } catch (e) {
          toast({
            ...DEFAULT_WARNING_TOAST_OPTIONS,
            description: (
              <ApiMessageStack
                messageStack={
                  "Credentials saved, but authorising with Jupix failed. Click Connect to retry."
                }
              />
            ),
          });
        }

        closePrompt();
        history.push(
          `${SETTINGS_INTEGRATIONS_ROUTE}/${AccountIntegrationTypeEnum.JUPIX}`
        );
      },
      [accountIntegrationsStore, closePrompt, toast, history]
    );

    return (
      <Box pt={5}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={5}>
            <FormControlV2<TJupixIntegrationConfigFieldValues>
              label={"Client Id"}
              type={FormControlsTypeEnum.TEXT}
              name={"clientId"}
              control={control}
            />
          </Box>
          <Box mb={5}>
            <FormControlV2<TJupixIntegrationConfigFieldValues>
              label={"Client Secret"}
              type={FormControlsTypeEnum.TEXT}
              name={"clientSecret"}
              control={control}
            />
          </Box>
          <Divider mb={4} />
          <Box
            width={"100%"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              type={"submit"}
              colorScheme={"blue"}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Save credentials
            </Button>
          </Box>
        </form>
      </Box>
    );
  }
);
